/**
 * Display-only grouping of consecutive agent messages in the same correlation turn.
 * Server event offsets and ids are preserved internally; the UI renders one stable bubble.
 */

import type { MessageInterface } from '../hooks/useMessageProcessing';
import { extractStreamingMessage } from '../types/parlant';

export function isAgentSource(source: string): boolean {
  return source === 'ai_agent' || source === 'human_agent';
}

export function buildTurnKey(correlationId: string, firstAgentOffset: number): string {
  return `turn-${correlationId}-${firstAgentOffset}`;
}

export function buildPendingTurnKey(correlationId: string): string {
  return `turn-${correlationId}-pending`;
}

function isCustomerSource(source: string): boolean {
  return source === 'customer' || source === 'customer_ui';
}

function createPlaceholderAgentTurn(
  correlationId: string,
  insertAfterOffset: number
): MessageInterface {
  return {
    id: buildPendingTurnKey(correlationId),
    kind: 'message',
    source: 'ai_agent',
    creationUtc: new Date(),
    offset: insertAfterOffset + 1,
    correlationId,
    data: { message: '', _placeholderTurn: true },
    status: 'typing',
  };
}

/**
 * Applies inline status to the grouped agent turn for a correlation id.
 * When no agent turn exists yet, inserts a lightweight placeholder bubble
 * directly after the matching customer message so status never jumps to a
 * separate bottom row.
 */
export function applyInlineStatusToGroupedTurns(
  grouped: MessageInterface[],
  inlineStatus: { correlationId: string; text: string } | null
): MessageInterface[] {
  if (!inlineStatus) {
    return grouped;
  }

  let targetIndex = -1;
  for (let i = grouped.length - 1; i >= 0; i -= 1) {
    const message = grouped[i];
    if (isAgentSource(message.source) && message.correlationId === inlineStatus.correlationId) {
      targetIndex = i;
      break;
    }
  }

  if (targetIndex >= 0) {
    return grouped.map((message, index) =>
      index === targetIndex ? { ...message, displayStatusText: inlineStatus.text } : message
    );
  }

  let insertAfterIndex = -1;
  for (let i = grouped.length - 1; i >= 0; i -= 1) {
    const message = grouped[i];
    if (isCustomerSource(message.source) && message.correlationId === inlineStatus.correlationId) {
      insertAfterIndex = i;
      break;
    }
  }

  const insertAfterOffset =
    insertAfterIndex >= 0 ? grouped[insertAfterIndex].offset : grouped.at(-1)?.offset ?? 0;
  const placeholder = {
    ...createPlaceholderAgentTurn(inlineStatus.correlationId, insertAfterOffset),
    displayStatusText: inlineStatus.text,
  };

  if (insertAfterIndex < 0) {
    return [...grouped, placeholder];
  }

  const withPlaceholder = [...grouped];
  withPlaceholder.splice(insertAfterIndex + 1, 0, placeholder);
  return withPlaceholder;
}

function getMessageText(message: MessageInterface): string {
  const data = message.data as Record<string, unknown>;
  const streamed = extractStreamingMessage(data);
  if (typeof streamed === 'string' && streamed.length > 0) {
    return streamed;
  }
  const raw = data?.message;
  return typeof raw === 'string' ? raw : '';
}

function isPreambleMessage(message: MessageInterface): boolean {
  const data = message.data as Record<string, unknown>;
  const tags = data?.tags;
  if (!Array.isArray(tags)) return false;
  return tags.includes('__preamble__');
}

/**
 * When the LLM continues after a preamble with a comma-prefixed line, move the
 * comma onto the preamble line so the second line does not start with punctuation.
 */
function normalizePreambleContinuation(
  preambleLine: string,
  continuationLine: string
): { preambleLine: string; continuationLine: string } {
  const commaContinuation = continuationLine.match(/^,\s*(.+)$/s);
  if (commaContinuation) {
    const withComma = preambleLine.endsWith(',') ? preambleLine : `${preambleLine},`;
    return { preambleLine: withComma, continuationLine: commaContinuation[1] };
  }
  return { preambleLine, continuationLine };
}

/**
 * Joins agent turn segments. Preamble-tagged segments always render on their own
 * line above the following segment (single newline, not a paragraph break).
 */
function joinAgentTurnTexts(group: MessageInterface[]): string {
  const entries = group
    .map(msg => ({
      text: getMessageText(msg).trim(),
      isPreamble: isPreambleMessage(msg),
    }))
    .filter(entry => entry.text.length > 0);

  if (entries.length === 0) return '';

  let joinedMessage = entries[0].text;

  for (let i = 1; i < entries.length; i += 1) {
    const prev = entries[i - 1];
    const curr = entries[i];

    if (prev.isPreamble) {
      const normalized = normalizePreambleContinuation(joinedMessage, curr.text);
      joinedMessage = `${normalized.preambleLine}\n${normalized.continuationLine}`;
    } else {
      joinedMessage += `\n\n${curr.text}`;
    }
  }

  return joinedMessage;
}

function mergeAgentTurnGroup(group: MessageInterface[]): MessageInterface {
  const first = group[0];
  const last = group[group.length - 1];
  const joinedMessage = joinAgentTurnTexts(group);
  const sourceEventIds = group.map(message => message.id).filter(Boolean) as string[];
  const turnKey = buildTurnKey(first.correlationId, first.offset);

  return {
    ...last,
    id: turnKey,
    turnKey,
    offset: first.offset,
    creationUtc: first.creationUtc,
    status: last.status,
    data: {
      ...last.data,
      message: joinedMessage,
      _sourceEventIds: sourceEventIds,
      _groupedTurn: true,
    },
  };
}

/**
 * Merges consecutive agent messages that share a correlation id into one display bubble.
 */
export function groupAgentTurns(messages: MessageInterface[]): MessageInterface[] {
  const result: MessageInterface[] = [];
  let index = 0;

  while (index < messages.length) {
    const message = messages[index];
    if (!isAgentSource(message.source)) {
      result.push(message);
      index += 1;
      continue;
    }

    const correlationId = message.correlationId;
    const group: MessageInterface[] = [message];
    let nextIndex = index + 1;

    while (nextIndex < messages.length) {
      const next = messages[nextIndex];
      if (isAgentSource(next.source) && next.correlationId === correlationId) {
        group.push(next);
        nextIndex += 1;
      } else {
        break;
      }
    }

    result.push(group.length === 1 ? message : mergeAgentTurnGroup(group));
    index = nextIndex;
  }

  return result;
}
